import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { BaseComponent } from '../../../shared/base-classes/base.component';
import { AppToastManagerService } from '../../../shared/services/toast-manager.service';
import { AppBehaviorCodeEntryService } from '../behavior-code-entry.service';
import {
  AnalyticsEventEnum,
  BehaviorCodeEntryCreateDto,
  BehaviorCodeEntryResponseDto,
  IRelation,
  PrivilegeEnum,
  ReactionTypeEnum,
  ReactionEntriesListResponseDto,
  StudentDetailResponseDto,
} from '@whetstoneeducation/hero-common';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { AppStudentsService } from '../../students/students.service';
import { Router } from '@angular/router';
import { AppPrivilegesService } from '../../auth/privileges.service';
import { IBehaviorCodeEntryCreateFormControlData } from '../behavior-code-entry-create-modal/behavior-code-entry-create.models';
import { map, Observable, startWith, Subject } from 'rxjs';
import { printPass } from 'src/app/shared/printer/browser-print.util';
import { PrinterService } from 'src/app/shared/printer/printer.service';
import { AppAnalyticsService } from 'src/app/shared/services/analytics.service';

@Component({
  selector: 'app-scan-track-modal',
  templateUrl: './scan-track-modal.component.html',
  styleUrls: ['./scan-track-modal.component.scss']
})
export class ScanTrackModalComponent
  extends BaseComponent
  implements OnInit, OnDestroy {

  public title: string = 'Scan Students';
  public scanning = false;
  public isLoading = false;
  public isSubmitDisabled = true;
  public studentExtId = new FormControl('');
  scannedBarcode = "";
  lastKeyPressTime = 0;
  SCAN_THRESHOLD_MS = 100;
  public browserPrint: boolean = false;

  public students: StudentDetailResponseDto[] = [];
  public behaviorCodeEntries: BehaviorCodeEntryResponseDto[] = [];
  public behaviorCodeTypeOptions: IRelation[];
  public behaviorCodeOptions: IRelation[];
  public behaviorCodeReactionOptions: IRelation[];

  public behaviorCodeTypeIdControl = new FormControl<IRelation>(
    null,
    Validators.required,
  );
  public behaviorCodeIdControl = new FormControl<IRelation>(
    null,
    Validators.required
  );
  public behaviorCodeReactionIdControl = new FormControl<IRelation>(
    null,
    Validators.required
  );
  public notesControl = new FormControl<string>('');
  public behaviorCodeEntryForms: FormGroup<IBehaviorCodeEntryCreateFormControlData>[];


  public filteredBehaviorTypes: Observable<IRelation[]>;
  public filteredBehaviorCodes: Observable<IRelation[]>;
  public filteredReactions: Observable<IRelation[]>;


  public onClose: () => void;

  private destroy$ = new Subject<void>();

  constructor(
    public toastService: AppToastManagerService,
    public behaviorCodeEntryService: AppBehaviorCodeEntryService,
    public studentsService: AppStudentsService,
    public privilegesService: AppPrivilegesService,
    private printer: PrinterService,
    public dialogRef: MatDialogRef<ScanTrackModalComponent>,
    public formBuilder: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      studentIds: number[];
      onClose?: () => void;
    },
    public router: Router,
    private analyticsService: AppAnalyticsService
  ) {
    super({ privilegesService });
    if (this.data.onClose) {
      this.onClose = this.data.onClose;
    }
  }

  private updateIsSubmitDisabled(): void {
    if (
      this.behaviorCodeReactionIdControl.value == null &&
      this.behaviorCodeReactionOptions?.length
    ) {
      this.isSubmitDisabled = this.behaviorCodeReactionIdControl.invalid;
    } else {
      this.isSubmitDisabled = this.behaviorCodeIdControl.invalid;
    }
  }

  async ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    const currentTime = new Date().getTime();
    if (currentTime - this.lastKeyPressTime > this.SCAN_THRESHOLD_MS) {
      this.scannedBarcode = "";
    }

    if (event.key === "Enter") {
      if (this.scannedBarcode && !this.isSubmitDisabled) {
        this.studentExtId.setValue(this.scannedBarcode);
        this.trackBehavior();
      }

      this.scannedBarcode = "";
    } else if (event.key.length === 1) {
      this.scannedBarcode += event.key;
    }

    this.lastKeyPressTime = currentTime;
  }

  async ngOnInit() {
    this.isLoading = true;
    try {
      const user = this.StorageManager.getLoggedInUser();
      this.behaviorCodeTypeOptions =
        await this.behaviorCodeEntryService.getBehaviorCodeTypesWithBehaviorCodes(
          user.schoolGroupId
        );
    } catch (error) {
      this.toastService.error(error.message);
      this.dialogRef.close();
    }

    this.isLoading = false;
    this.updateIsSubmitDisabled();

    this.behaviorCodeTypeIdControl.valueChanges.subscribe(async (value) => {
      if (value?.id) {
        this.isLoading = true;
        try {
          this.behaviorCodeOptions =
            await this.behaviorCodeEntryService.getBehaviorCodesByType(value.id);
        } catch (error) {
          this.toastService.error(error.message);
          this.dialogRef.close();
        }
        this.isLoading = false;
        this.updateIsSubmitDisabled();
      } else {
        this.behaviorCodeOptions = [];
      }
    });

    this.filteredBehaviorTypes = this.behaviorCodeTypeIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this._filter(value, 'behaviorCodeTypeOptions');
      })
    );

    this.behaviorCodeIdControl.valueChanges.subscribe(async (value) => {

      this.behaviorCodeReactionOptions =
        await this.behaviorCodeEntryService.getReactionOptions(
          value.id,
          0,
          ReactionTypeEnum.SELECT_MENU
        );
      this.updateIsSubmitDisabled();
    });

    this.filteredBehaviorCodes = this.behaviorCodeIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this._filter(value, 'behaviorCodeOptions');
      })
    );

    this.filteredReactions = this.behaviorCodeReactionIdControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        return this._filter(value, 'behaviorCodeReactionOptions');
      })
    );

    this.behaviorCodeReactionIdControl.valueChanges.subscribe(() => {
      this.updateIsSubmitDisabled();
    });

    this.notesControl.valueChanges.subscribe(() => {
      this.updateIsSubmitDisabled();
    });
  }

  private _filter(value: string | IRelation, options: string): IRelation[] {
    if (!this[options]) {
      return [];
    } else if (!value || typeof value !== 'string') {
      return this[options];
    }

    const filterValue = value?.toLowerCase();

    return this[options].filter((option) => option.name.toLowerCase().includes(filterValue));
  }

  displaySelectedOption(options: string, optionSelected: IRelation) {
    if (this.isLoading) {
      return '';
    }
    const selection = this[options].find(item => item.id === optionSelected?.id);
    return selection?.name || '';
  }

  public onClear(control: string) {
    this[control].setValue('');
  }

  async submit(studentId: number) {
    this.isLoading = true;
    try {
      const date = new Date();
      const entry = new BehaviorCodeEntryCreateDto({
        notes: this.notesControl.value,
        studentId: studentId,
        behaviorCodeId: this.behaviorCodeIdControl.value.id,
        time: date.getTime(),
        schoolId: this.StorageManager.getLoggedInUser().currentSchoolId,
        behaviorCodeReactionId: this.behaviorCodeReactionIdControl.value?.id,
      });

      const entriesCreated = await this.behaviorCodeEntryService.batchCreateBehaviorCodeEntries([entry]);
      const behaviorCodeEntriesIds = entriesCreated.map(entry => entry.id);
      this.printOnPass(behaviorCodeEntriesIds);

    } catch (error: any) {
      this.toastService.error(error.message || 'Unexpected error');
    } finally {
      this.isLoading = false;
    }
  }

  async printOnPass(behaviorCodeEntryIds: number[]) {
    try {
      const printData = await this.behaviorCodeEntryService.getBehaviorCodeEntryConfirmReactionsResolverData(
        behaviorCodeEntryIds,
        this.StorageManager.getLoggedInUser().currentSchoolId,
        true
      );

      const reactionEntries = printData.reactionEntries.results;
      if (!reactionEntries?.length) return;
      const behaviorCodeId = reactionEntries[0].behaviorCodeId;
      const behaviorCode = behaviorCodeId
        ? printData.behaviorCodes.get(behaviorCodeId)
        : null;
      if (this.behaviorCodeEntryService.printsPass(reactionEntries[0], behaviorCode)) {
        const dialog = await this.printer.initializeDevice();
        if (dialog) {
          dialog.afterClosed().subscribe((browserPrint) => {
            this.browserPrint = browserPrint;
            this.printSingleReaction(reactionEntries);
          });
        } else {
          this.printSingleReaction(reactionEntries);
        }
      }
    } catch {
      this.toastService.error('Error printing reaction, printer not found');
    }
  }

  private async printSingleReaction(reactionEntries: ReactionEntriesListResponseDto[]) {
    if (reactionEntries.length === 1 && this.printer.usbAvailable) {
      try {
        
        const printTrack = await this.behaviorCodeEntryService.generatePrintData(
          this.printer.configuration.languageType,
          reactionEntries[0]
        );

        for (let i = 0; i < reactionEntries[0].printCopies; i++) {
          this.printer.addToPrintQueue(printTrack);
        }

      } catch (error) {
        this.toastService.error('Error printing reaction, printer not found');
      }
    } else if (!this.printer.usbAllowed || this.browserPrint) {
      printPass(reactionEntries[0]);
      this.analyticsService.track(
        AnalyticsEventEnum.PASS_PRINTED,
        reactionEntries[0].id
      );
    }
  }

  toastAndClose() {
    this.toastService.info('Scanning stopped. Start new scan anytime.');
    this.dialogRef.close();
  }

  onScan() {
    if (this.scanning && !this.isSubmitDisabled) {
      this.toastAndClose();
    }

    this.scanning = !this.scanning;
    this.behaviorCodeTypeIdControl.disable();
    this.behaviorCodeIdControl.disable(); this.notesControl.disable();
    this.behaviorCodeReactionIdControl.disable();
  }

  async trackBehavior() {
    try {
      const student = await this.studentsService.getStudentByExtId(Number(this.studentExtId.value));
      await this.submit(student.id);
      this.toastService.success(`${student.firstName} ${student.lastName} has been tracked successfully`)
    } catch (error) {
      this.toastService.error('Couldn’t complete the scan. Try again or enter Ext. Student ID Manually.')
    } finally {
      this.studentExtId.setValue('');
    }
  }

  protected readonly PrivilegeEnum = PrivilegeEnum;
}
